// Public islands entry (SPEC-01 §4.5): scans [data-widget] and mounts the
// matching React component over the server-rendered fallback. No island
// carries critical content — every page is complete without JS.
import "./public.css";
import "./marketplace-catalog.css";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import SearchBox from "./SearchBox.jsx";
import FilterDrawer from "./FilterDrawer.jsx";
import ScreenshotGallery from "./ScreenshotGallery.jsx";
import DetailTabs from "./DetailTabs.jsx";
import CopyPrompt from "./CopyPrompt.jsx";
import StoreSwitch from "./StoreSwitch.jsx";
import { enhanceAnalytics } from "./analytics.js";
import { enhanceEvidenceTips } from "./evidence-tooltip.js";

const WIDGETS = {
  "search-box": SearchBox,
  "filter-drawer": FilterDrawer,
  "screenshot-gallery": ScreenshotGallery,
  "detail-tabs": DetailTabs,
  "copy-prompt": CopyPrompt,
  "store-switch": StoreSwitch,
};

function mountIslands() {
  document.querySelectorAll("[data-widget]").forEach((node) => {
    const name = node.getAttribute("data-widget");
    const Component = WIDGETS[name];
    if (!Component || node.dataset.mounted) return;
    node.dataset.mounted = "1";
    let props = {};
    const raw = node.getAttribute("data-props");
    if (raw) {
      try {
        props = JSON.parse(raw);
      } catch {
        // Bad island props must never break the page.
      }
    }
    createRoot(node).render(createElement(Component, props));
  });
}

function enhanceMarketplaceIcons() {
  document.querySelectorAll("img[data-marketplace-icon]").forEach((image) => {
    const fallback = image.parentElement?.querySelector(".marketplace-catalog__icon-fallback");
    if (!fallback) return;
    const showFallback = () => {
      image.hidden = true;
      fallback.hidden = false;
    };
    const showImage = () => {
      image.hidden = false;
      fallback.hidden = true;
    };
    image.addEventListener("error", showFallback, { once: true });
    image.addEventListener("load", showImage, { once: true });
    if (image.complete) {
      if (image.naturalWidth > 0) showImage();
      else showFallback();
    }
  });
}

// Market/platform chips: persist the choice so later navigations keep it
// (SPEC-01 §7.1; no geo-IP in phase 1). Links already carry query params,
// so this only records intent — SSR stays the source of truth.
function enhanceChips() {
  document.querySelectorAll(".chip__menu a[data-market]").forEach((link) => {
    link.addEventListener("click", () => {
      try {
        localStorage.setItem("appcorpus:market", link.getAttribute("data-market") || "");
      } catch {
        // Storage unavailable (private mode) — navigation still works.
      }
    });
  });
  document.querySelectorAll(".chip__menu a[data-platform]").forEach((link) => {
    link.addEventListener("click", () => {
      try {
        localStorage.setItem("appcorpus:platform", link.getAttribute("data-platform") || "");
      } catch {
        // Storage unavailable — navigation still works.
      }
    });
  });
}

function syncStickyOffsets() {
  const header = document.querySelector(".pub-header");
  const toc = document.querySelector(".app-toc");
  const root = document.documentElement;
  if (header) root.style.setProperty("--pub-header-h", `${header.offsetHeight}px`);
  if (toc) root.style.setProperty("--app-toc-h", `${toc.offsetHeight}px`);
}

function restoreHashTarget() {
  const id = decodeURIComponent(location.hash.replace(/^#/, ""));
  if (!id) return;
  document.getElementById(id)?.scrollIntoView({ block: "start", behavior: "auto" });
}

function watchStickyOffsets() {
  syncStickyOffsets();
  restoreHashTarget();
  if (typeof ResizeObserver === "undefined") return;
  const observer = new ResizeObserver(syncStickyOffsets);
  const header = document.querySelector(".pub-header");
  const toc = document.querySelector(".app-toc");
  if (header) observer.observe(header);
  if (toc) observer.observe(toc);
}

// Dropdowns are <details> elements (work without JS); this adds the
// standard dismiss behavior — outside click and Escape close them.
function enhanceDropdowns() {
  const openDetails = () => Array.from(document.querySelectorAll(".pub-header details[open]"));
  document.addEventListener("click", (event) => {
    openDetails().forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute("open");
    });
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    openDetails().forEach((details) => details.removeAttribute("open"));
  });
  document.querySelector("[data-nav-toggle]")?.addEventListener("toggle", syncStickyOffsets);
}

// Theme toggle: flips <html data-theme>, mirrors the choice into the
// cookie the server reads on the next SSR render (no flash) and into the
// key the internal console reads.
function enhanceThemeToggle() {
  document.querySelectorAll("[data-theme-toggle]").forEach((button) => {
    button.addEventListener("click", () => {
      const current = document.documentElement.dataset.theme
        || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light");
      const next = current === "dark" ? "light" : "dark";
      document.documentElement.dataset.theme = next;
      document.cookie = `appcorpus_theme=${next}; Path=/; Max-Age=31536000; SameSite=Lax`;
      try {
        localStorage.setItem("atlas-theme", next);
      } catch {
        // Storage unavailable — the cookie already persists the choice.
      }
    });
  });
}

function restoreMarketPlatform() {
  const url = new URL(window.location.href);
  if (url.searchParams.has("market") || url.searchParams.has("platform")) return;
  let market = "";
  let platform = "";
  try {
    market = localStorage.getItem("appcorpus:market") || "";
    platform = localStorage.getItem("appcorpus:platform") || "";
  } catch {
    return;
  }
  if (market && market !== "us") url.searchParams.set("market", market);
  // App page has no platform filter (SPEC-03 §4.1); do not rewrite its URL.
  if (platform && !/^\/apps\/[a-z0-9-]+-\d+\/?$/.test(url.pathname)) {
    url.searchParams.set("platform", platform);
  }
  if (url.search !== window.location.search) {
    window.location.replace(url.pathname + url.search + url.hash);
  }
}

function enhanceSortSelect() {
  document.querySelector("[data-sort-select]")?.addEventListener("change", (event) => {
    const value = event.target.value;
    if (value) window.location.href = value;
  });
}

function logSearchDebug() {
  const el = document.querySelector("[data-search-debug]");
  if (!el) return;
  const flag = (value) => value === "true";
  console.log("[appathlas:search]", {
    search_mode: el.dataset.searchMode || "browse",
    query_class: el.dataset.queryClass || "browse",
    query: el.dataset.query || "",
    total: Number(el.dataset.total || 0),
    page: Number(el.dataset.page || 1),
    pages: Number(el.dataset.pages || 1),
    nav_kind: el.dataset.navKind || "",
    sort: el.dataset.sort || "",
    truncated: flag(el.dataset.truncated),
    hide_facet_counts: flag(el.dataset.hideFacetCounts),
    zero_result: flag(el.dataset.zeroResult),
    lang: el.dataset.lang || "en",
    filters: Number(el.dataset.filters || 0),
    market: el.dataset.market || "",
  });
}

function enhanceLoadMore() {
  const root = document.querySelector(".catalog");
  if (!root || root.dataset.navKind !== "load_more") return;
  if (!window.__appathlasLoadMorePopstate) {
    window.__appathlasLoadMorePopstate = true;
    window.addEventListener("popstate", () => {
      if (document.querySelector(".catalog[data-nav-kind='load_more']")) {
        window.location.reload();
      }
    });
  }
  bindLoadMore(root);
}

function bindLoadMore(root) {
  const nav = root.querySelector("[data-load-more]");
  const link = nav && nav.querySelector("a.pagination__load-more");
  if (!link || link.dataset.bound) return;
  link.dataset.bound = "1";
  link.addEventListener("click", (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    loadMorePage(root, link);
  });
}

async function loadMorePage(root, link) {
  const url = link.getAttribute("href");
  if (!url || link.getAttribute("aria-busy") === "true") return;
  const idleLabel = link.getAttribute("data-idle-label") || "Load more";
  const loadingLabel =
    root.querySelector("[data-load-more-live]")?.getAttribute("data-loading") || "Loading more apps";
  link.setAttribute("aria-busy", "true");
  link.setAttribute("aria-label", loadingLabel);
  try {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "text/html", "X-Requested-With": "fetch" },
    });
    if (!response.ok) throw new Error("load-more");
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const nextGrid = doc.querySelector(".catalog__grid");
    const grid = root.querySelector(".catalog__grid");
    if (!nextGrid || !grid) throw new Error("load-more");
    const existing = new Set(
      Array.from(grid.querySelectorAll("[data-app-id]")).map((node) => node.getAttribute("data-app-id"))
    );
    let added = 0;
    let firstNew = null;
    // Blur first: a focused Load more button is a scroll anchor, so appending
    // cards above it would drag the viewport down to the button.
    link.blur();
    Array.from(nextGrid.children).forEach((card) => {
      const id = card.getAttribute("data-app-id");
      if (id && existing.has(id)) return;
      if (id) existing.add(id);
      const imported = document.importNode(card, true);
      grid.appendChild(imported);
      if (!firstNew) firstNew = imported;
      added += 1;
    });
    history.pushState({}, "", url);
    const fetched = doc.querySelector(".catalog");
    if (fetched) {
      root.dataset.page = fetched.dataset.page || root.dataset.page;
      root.dataset.pages = fetched.dataset.pages || root.dataset.pages;
      root.dataset.total = fetched.dataset.total || root.dataset.total;
    }
    const live = root.querySelector("[data-load-more-live]");
    if (live) live.textContent = live.getAttribute("data-status") || "";
    const nextHref = doc.querySelector("[data-load-more] a.pagination__load-more")?.getAttribute("href");
    if (nextHref) {
      link.setAttribute("href", nextHref);
    } else {
      root.querySelector("[data-load-more]")?.remove();
      if (firstNew) {
        firstNew.setAttribute("tabindex", "-1");
        firstNew.focus({ preventScroll: true });
      }
    }
    if (!added && url) window.location.href = url;
  } catch {
    window.location.href = url;
  } finally {
    if (link.isConnected) {
      link.removeAttribute("aria-busy");
      link.setAttribute("aria-label", idleLabel);
    }
  }
}

function rememberLastQuery() {
  document.querySelectorAll('form[action="/apps"]').forEach((form) => {
    form.addEventListener("submit", () => {
      const input = form.querySelector('input[name="q"]');
      const value = (input && input.value ? input.value : "").trim();
      if (!value) return;
      try {
        localStorage.setItem("appcorpus:last-query", value);
      } catch {
        // Storage unavailable — search still navigates.
      }
    });
  });
}

if (typeof document !== "undefined") {
  const boot = () => {
    restoreMarketPlatform();
    mountIslands();
    enhanceMarketplaceIcons();
    enhanceChips();
    enhanceDropdowns();
    enhanceThemeToggle();
    enhanceSortSelect();
    enhanceLoadMore();
    logSearchDebug();
    rememberLastQuery();
    watchStickyOffsets();
    enhanceAnalytics();
    enhanceEvidenceTips();
    window.addEventListener("resize", syncStickyOffsets);
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
}
